import { dateOnly, DateOnlyInput } from './dateUtils';
import {
  DailyAvailabilityDay,
  DailyAvailabilityLedgerEntry,
} from '../types/DailyAvailability';
import { InventoryLedgerEntry } from '../types/InventoryLedgerEntry';

type AddOnLedgerEntry = Pick<
  InventoryLedgerEntry,
  'date' | 'quantity' | 'balanceAfter'
>;

export type AddOnAvailabilityHold = {
  available?: number;
  ledgerEntries?: AddOnLedgerEntry[];
};

export type ProductAvailabilityKind = 'default' | 'bundles' | 'addOns';

export interface DailyAvailabilityFlags {
  numberAvailable: number;
  isRentedInPeriod: boolean;
  overbooked: boolean;
}

export const getProductAvailabilityKind = (
  productTypeKey: string
): ProductAvailabilityKind => {
  if (productTypeKey === 'bundles') {
    return 'bundles';
  }
  if (productTypeKey === 'addOns') {
    return 'addOns';
  }
  return 'default';
};

export const isAddOnRentedOnDate = (
  ledgerEntries: DailyAvailabilityLedgerEntry[] | undefined,
  dateTimestamp: string
): boolean =>
  Boolean(
    ledgerEntries?.length &&
      ledgerEntries.some(
        (entry) => dateOnly(entry.date) === dateOnly(dateTimestamp)
      )
  );

export const computeDailyAvailabilityFlags = (
  availabilityData: DailyAvailabilityDay,
  dateTimestamp: string,
  productTypeKey: string
): DailyAvailabilityFlags => {
  const {
    available,
    unavailable,
    maintenance,
    ledgerEntries,
    inUse,
    rawAvailableCount = 0,
  } = availabilityData;

  const numberAvailable = available - (maintenance || 0);
  let isRentedInPeriod = unavailable > 0;
  let overbooked = numberAvailable < 0;

  const kind = getProductAvailabilityKind(productTypeKey);

  if (kind === 'bundles') {
    isRentedInPeriod = Boolean(inUse);
    overbooked = rawAvailableCount < 0;
  } else if (kind === 'addOns') {
    isRentedInPeriod = isAddOnRentedOnDate(ledgerEntries, dateTimestamp);
  }

  return { numberAvailable, isRentedInPeriod, overbooked };
};

const toTimestamp = (value: DateOnlyInput): number | null => {
  if (value == null) {
    return null;
  }

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
};

// Add-ons track on-hand balance via signed ledger entries. For rental availability,
// the relevant balance is the running total at the order's off-shelf time (excluding
// this order's own ledger rows, which the availability API already filters out).
export const getAddOnBalanceAtOffShelf = (
  itemHold: AddOnAvailabilityHold | null | undefined,
  offShelfAt: DateOnlyInput
): number | null => {
  const ledgerEntries = itemHold?.ledgerEntries;
  const offShelfTimestamp = toTimestamp(offShelfAt);

  if (!ledgerEntries?.length || offShelfTimestamp == null) {
    return null;
  }

  let balanceAtOffShelf: number | null = null;
  let runningBalance = 0;

  for (const entry of ledgerEntries) {
    const entryTimestamp = toTimestamp(entry.date);
    if (entryTimestamp == null || entryTimestamp > offShelfTimestamp) {
      break;
    }

    runningBalance =
      entry.balanceAfter != null
        ? entry.balanceAfter
        : runningBalance + entry.quantity;
    balanceAtOffShelf = runningBalance;
  }

  return balanceAtOffShelf ?? 0;
};

export const getAddOnNumberAvailable = (
  itemHold: AddOnAvailabilityHold | null | undefined,
  offShelfAt: DateOnlyInput
): number => {
  const balanceAtOffShelf = getAddOnBalanceAtOffShelf(itemHold, offShelfAt);

  if (balanceAtOffShelf != null) {
    return balanceAtOffShelf;
  }

  return itemHold?.available || 0;
};
